// src/components/Dashboard/Dashboard.js - Fixed version with proper data isolation
import VisualFinanceIntegration from '../Chat/VisualFinanceIntegration';
import DirectDataIntegration from './DirectDataIntegration';
import DataIntegrationModeSelector from './DataIntegrationModeSelector';
import InlineQueryHelper from './InlineQueryHelper';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Nav, Form, InputGroup, Badge, Dropdown, Spinner, Alert } from 'react-bootstrap';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { insightsService } from '../../services/insights';
import { useAuth } from '../../context/AuthContext';
import Documentation from '../Documentation/Documentation';
import './Dashboard.css';
import APIKeysManagement from '../APITokenManagement';
import { EventSourcePolyfill } from 'event-source-polyfill';
import PlaidLinkButton from '../Plaid/PlaidLinkButton';
import logger from '../../utils/logger';
import api from '../../services/api';
import PlaidDataSidebar from '../Dashboard/PlaidDataSidebar';
import ModernDashboardHome from './ModernDashboardHome';
import PrivacyAgreementModal from '../Account/PrivacyAgreementModal';

// Simple API Status component
const PlaidApiStatus = () => {
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    const checkApi = async () => {
      try {
        // First try the health endpoint since /plaid/status might not exist yet
        const response = await api.get('/health');

        if (response.data && response.status === 200) {
          setStatus('connected');

          // Also try to check plaid status, but don't fail if it's not available
          try {
            await api.get('/plaid/status');
          } catch (plaidErr) {
            // Just log this error, don't change the status
            logger.info('Plaid status endpoint not available yet');
          }
        } else {
          setStatus('error');
        }
      } catch (error) {
        logger.error('API health check failed:', error);
        setStatus('error');
      }
    };

    checkApi();
  }, []);

  return (
    <Badge
      bg={status === 'checking' ? 'secondary' : status === 'connected' ? 'success' : 'danger'}
      className="d-flex align-items-center"
    >
      <span className="status-indicator me-1"></span>
      API: {status === 'checking' ? 'Checking...' : status === 'connected' ? 'Online' : 'Error'}
    </Badge>
  );
};

const Dashboard = () => {
  const { user, clientStatus, logout } = useAuth();
  const [activeSection, setActiveSection] = useState('home');
  const [apiKey, setApiKey] = useState(() => {
    return user?.token || localStorage.getItem('token') || 'No API key found';
  });
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! I am your Banking Intelligence Assistant. How can I help you with your financial data today?',
      timestamp: new Date().toISOString()
    }
  ]);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError] = useState(null);
  const [insightsData, setInsightsData] = useState(null);

  // Add a ref to track the latest request ID
  const latestRequestIdRef = useRef(null);
  const chatEndRef = useRef(null);

  // Add Plaid connection state
  const [connected, setConnected] = useState(false);
  const [institution, setInstitution] = useState('');
  const [connectionSuccess, setConnectionSuccess] = useState(false);

  // Add state for the financial data sidebar
  const [showDataSidebar, setShowDataSidebar] = useState(false);
  const [financialData, setFinancialData] = useState(null);
  const [loadingFinancialData, setLoadingFinancialData] = useState(false);

  // Add state for tracking Plaid connection status
  const [plaidStatus, setPlaidStatus] = useState('ready');
  const [plaidError, setPlaidError] = useState(null);

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // State for visualizations
  const [visualizationsVisible, setVisualizationsVisible] = useState(true);

  // State for integration mode
  const [integrationMode, setIntegrationMode] = useState(() => {
    // Try to get from localStorage first, default to 'plaid'
    return localStorage.getItem('integrationMode') || 'plaid';
  }); // 'plaid' or 'direct'
  const [directFinancialData, setDirectFinancialData] = useState(null);

  const [selectorExpanded, setSelectorExpanded] = useState(true);

  // Handler to toggle between integration modes
  const handleIntegrationModeChange = (mode) => {
    // Only make changes if the mode is actually changing
    if (mode === integrationMode) return;

    logger.info(`Switching integration mode from ${integrationMode} to ${mode}`);

    // Persist the mode to localStorage for API consistency
    localStorage.setItem('integrationMode', mode);

    // Update the connection status in localStorage too
    localStorage.setItem('plaidConnected', connected ? 'true' : 'false');

    // Set the new mode in state
    setIntegrationMode(mode);

    // Add a system message to the chat showing mode change
    setChatMessages(prev => [...prev, {
      role: 'assistant',
      content: mode === 'plaid'
        ? `I've switched to Plaid Integration mode. ${connected ? `I'm now using your connected bank data from ${institution}.` : "Connect a bank to use real financial data."}`
        : "I've switched to Direct Data Integration mode. I'm now using the simulated financial data that you can customize.",
      timestamp: new Date().toISOString(),
      isSystemMessage: true
    }]);

    // IMPORTANT: Break the data connection between modes
    // This is the key fix - we must completely separate data between modes
    if (mode === 'plaid') {
      // In Plaid mode, sidebar visibility depends on connection status
      setShowDataSidebar(connected);

      // If connected, fetch fresh Plaid data
      if (connected) {
        fetchFinancialData();
      } else {
        // Important: Set financialData to null, not directFinancialData
        setFinancialData(null);
      }
    } else if (mode === 'direct') {
      // In Direct mode, always show sidebar
      setShowDataSidebar(true);

      // CRITICAL FIX: Create a deep copy of directFinancialData to break any references to Plaid data
      // This prevents data leakage between modes
      const directDataCopy = directFinancialData ? JSON.parse(JSON.stringify(directFinancialData)) : null;
      setFinancialData(directDataCopy);
    }
  };

  // Add handler for direct data changes
  const handleDirectDataChange = (newData) => {
    // Always create a deep copy to prevent reference issues
    const newDataCopy = newData ? JSON.parse(JSON.stringify(newData)) : null;

    // Set the direct data state
    setDirectFinancialData(newDataCopy);

    // Update if this data is being used in the chat (we're in direct mode)
    if (integrationMode === 'direct') {
      // Create another deep copy to ensure complete isolation
      const financialDataCopy = newDataCopy ? JSON.parse(JSON.stringify(newDataCopy)) : null;
      setFinancialData(financialDataCopy);

      // IMPORTANT: Add a system message to notify the user that data has changed
      setChatMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: `I've updated my knowledge with your changed financial data. You now have ${financialDataCopy?.accounts?.length || 0} accounts with a total balance of $${calculateTotalBalance(financialDataCopy).toFixed(2)}.`,
          timestamp: new Date().toISOString(),
          isSystemMessage: true
        }
      ]);

      // Log the data update for debugging
      logger.info("Financial data updated:", financialDataCopy);
    }
  };

  const calculateTotalBalance = (data) => {
    if (!data || !data.accounts || !Array.isArray(data.accounts)) {
      return 0;
    }

    return data.accounts.reduce((total, account) => {
      // For credit accounts, use available balance (limit - current)
      if (account.type === 'credit') {
        return total + (account.balances.available || 0);
      }
      // For regular accounts, use current balance
      return total + (account.balances.current || 0);
    }, 0);
  };

  // Handler for clearing chat messages
  const handleClearChat = () => {
    // Add confirmation dialog
    if (window.confirm('Are you sure you want to clear all chat messages?')) {
      // Reset to initial welcome message
      setChatMessages([
        {
          role: 'assistant',
          content: `Hello ${user?.email || ''}! I am your Banking Intelligence Assistant. How can I help you with your financial data today?`,
          timestamp: new Date().toISOString()
        }
      ]);

      // Scroll to bottom after clearing
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  // Handler for clearing visualizations
  const handleClearVisualizations = () => {
    setVisualizationsVisible(false);

    // Re-enable visualizations after a short delay
    // This allows for properly clearing the current visualizations
    setTimeout(() => {
      setVisualizationsVisible(true);
    }, 300);
  };

  // Sample suggested prompts
  const suggestedPrompts = [
    "How much did I spend on dining out last month?",
    "What are my top expense categories?",
    "How can I improve my savings rate?",
    "Am I on track with my budget this month?"
  ];

  // Connected account prompts
  const connectedPrompts = [
    "How much did I spend on dining out last month?",
    "What's my current account balance?",
    "How can I improve my savings?",
    "Am I spending more on groceries than average?"
  ];

  useEffect(() => {
    // Update the connection status in localStorage when it changes
    localStorage.setItem('plaidConnected', connected ? 'true' : 'false');
  }, [connected]);

  // Reset all state when user changes - IMPORTANT for data isolation
  useEffect(() => {
    if (user) {
      // Reset all financial data when user changes
      setFinancialData(null);
      setConnected(false);
      setInstitution('');
      setShowDataSidebar(false);

      // Reset chat messages to initial state for new user
      setChatMessages([
        {
          role: 'assistant',
          content: `Hello ${user.email || ''}! I am your Banking Intelligence Assistant. How can I help you with your financial data today?`,
          timestamp: new Date().toISOString()
        }
      ]);

      // Check for existing connections for THIS user
      checkExistingConnections();
    }
  }, [user?.id]); // Only trigger when user ID changes

  // Add useEffect to manage body class for playground
  useEffect(() => {
    const managePlaygroundBodyClass = () => {
      const body = document.body;

      if (activeSection === 'playground') {
        // Add playground-active class when on playground
        body.classList.add('playground-active');

        // Also add mobile class specifically
        if (window.innerWidth <= 991) {
          body.classList.add('mobile-playground');
        }
      } else {
        // Remove playground classes when not on playground
        body.classList.remove('playground-active', 'mobile-playground');
      }
    };

    // Run immediately
    managePlaygroundBodyClass();

    // Also run on window resize to handle orientation changes
    const handleResize = () => {
      managePlaygroundBodyClass();
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      // Remove classes when component unmounts
      document.body.classList.remove('playground-active', 'mobile-playground');
    };
  }, [activeSection]);

  // Optional: Add mobile footer toggle functionality
  const [mobileFooterVisible, setMobileFooterVisible] = useState(false);

  const toggleMobileFooter = () => {
    setMobileFooterVisible(!mobileFooterVisible);
  };

  // Function to detect if user scrolled to bottom (optional)
  const handleScroll = (e) => {
    if (activeSection === 'playground' && window.innerWidth <= 991) {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

      // Show footer when near bottom
      if (isNearBottom && !mobileFooterVisible) {
        setMobileFooterVisible(true);
      }
    }
  };

  // Fetch financial data from Plaid
  const fetchFinancialData = useCallback(async () => {
    // Don't fetch if not connected or no user
    if (!connected || !user?.id) return;

    try {
      setLoadingFinancialData(true);
      logger.info(`Fetching financial data from Plaid for user ${user.id}`);

      // Add auth timestamp to URL to prevent caching and ensure fresh data
      const timestamp = Date.now();

      // Fetch accounts data with user validation
      const accountsResponse = await api.get(`/plaid/accounts?_t=${timestamp}`);

      // Fetch transactions data with user validation
      const transactionsResponse = await api.get(`/plaid/transactions?_t=${timestamp}`);

      if (accountsResponse.data.success && transactionsResponse.data.success) {
        const combinedData = {
          accounts: accountsResponse.data.data || [],
          transactions: transactionsResponse.data.data || [],
          institution: institution,
          lastUpdated: new Date().toISOString(),
          userId: user.id // Store user ID with the data for validation
        };

        // Store the data in component state
        setFinancialData(combinedData);
        setShowDataSidebar(true);

        logger.info(`Financial data fetched for user ${user.id}: ${combinedData.accounts.length} accounts, ${combinedData.transactions.length} transactions`);
      } else {
        throw new Error('Failed to retrieve complete financial data');
      }
    } catch (error) {
      logger.error(`Error fetching financial data for user ${user.id}:`, error);
      logger.error('Error fetching financial data:', error);

      // Don't disable sidebar if we have partial data
      if (!financialData) {
        setShowDataSidebar(false);
      }
    } finally {
      setLoadingFinancialData(false);
    }
  }, [connected, institution, user?.id]);

  // Check for existing Plaid connections on component mount or when user changes
  const checkExistingConnections = useCallback(async () => {
    if (!user?.id) return;

    try {
      logger.info(`Checking existing Plaid connections for user ${user.id}`);
      // Check if we already have accounts for THIS user
      const response = await api.get('/plaid/status');

      if (response.data.success && response.data.data.connected) {
        // We have existing accounts for this user, set the connected state
        setConnected(true);
        setShowDataSidebar(true);

        // Get institution information if available
        if (response.data.data.institution) {
          setInstitution(response.data.data.institution);
        }

        // Fetch financial data for THIS user
        fetchFinancialData();

        logger.info(`Found existing Plaid connection for user ${user.id} to ${response.data.data.institution || 'a bank'}`);
      } else {
        // No connected accounts for this user
        setConnected(false);
        setShowDataSidebar(false);
        setFinancialData(null);
        logger.info(`No existing Plaid connections found for user ${user.id}`);
      }
    } catch (error) {
      // No connected accounts or error fetching
      logger.error(`Error checking existing connections for user ${user.id}:`, error);
      setConnected(false);
      setShowDataSidebar(false);
      setFinancialData(null);
    }
  }, [user?.id, fetchFinancialData]);

  useEffect(() => {
    // When integration mode changes or when directFinancialData updates
    if (integrationMode === 'plaid') {
      // In Plaid mode: show sidebar only if connected
      setShowDataSidebar(connected);

      // If connected, use Plaid data
      if (connected) {
        // Fetch fresh data from Plaid to ensure we have the latest
        fetchFinancialData();
      } else {
        // Clear financial data if not connected
        setFinancialData(null);
      }
    } else if (integrationMode === 'direct') {
      // In Direct mode: always show the sidebar
      setShowDataSidebar(true);

      // CRITICAL FIX: Create a deep copy of directFinancialData to break any references
      const directDataCopy = directFinancialData ? JSON.parse(JSON.stringify(directFinancialData)) : null;

      // IMPORTANT: Always set financialData to a copy of directFinancialData in direct mode
      // This breaks any reference to Plaid data
      setFinancialData(directDataCopy);

      // Debug logging
      logger.info("Direct mode active, financial data set to:", directDataCopy);
    }

    // Update localStorage with current mode
    localStorage.setItem('integrationMode', integrationMode);
  }, [integrationMode, connected, directFinancialData, fetchFinancialData]);

  useEffect(() => {
    // Scroll to bottom of chat when messages change
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Check Plaid status and user's connections on mount and when user changes
  useEffect(() => {
    if (user?.id) {
      checkExistingConnections();
    }
  }, [user?.id, checkExistingConnections]);

  // Clean up function to clear state when component unmounts
  useEffect(() => {
    return () => {
      // Clear any cached data when dashboard unmounts
      setFinancialData(null);
      setConnected(false);

      // Clear any in-progress requests
      latestRequestIdRef.current = null;
    };
  }, []);

  // Add this useEffect to initialize direct data when needed
  useEffect(() => {
    // Initialize direct data if we're in direct mode and there's no data yet
    if (integrationMode === 'direct' && !directFinancialData) {
      // Create a default dataset if needed
      const defaultData = {
        // Your default data structure here
        accounts: [],
        transactions: [],
        institution: 'Demo Bank',
        lastUpdated: new Date().toISOString(),
        userId: user?.id
      };

      // Set the direct data
      setDirectFinancialData(defaultData);

      // Show the sidebar
      setShowDataSidebar(true);
    }
  }, [integrationMode, directFinancialData, user?.id]);

  // Generate a unique request ID
  const generateRequestId = () => {
    return `req_${user?.id || 'nouser'}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Handle Plaid connection success
  const handlePlaidSuccess = ({ itemId, metadata }) => {
    try {
      // Log detailed success info
      logger.info(`Bank account connected successfully for user ${user?.id}`, {
        institution: metadata.institution.name,
        accountCount: metadata.accounts.length,
        itemId: itemId
      });

      // Save institution name for display
      setInstitution(metadata.institution.name);

      // Mark as connected
      setConnected(true);

      // Explicitly set showDataSidebar to true
      setShowDataSidebar(true);

      // Show success message
      setConnectionSuccess(true);
      setTimeout(() => setConnectionSuccess(false), 5000); // Hide after 5 seconds

      // Add a system message showing connection success
      setChatMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: `I've successfully connected to your accounts at ${metadata.institution.name}. You can now ask questions about your actual financial data!`,
          timestamp: new Date().toISOString()
        }
      ]);

      // Fetch financial data after successful connection
      fetchFinancialData();
    } catch (err) {
      logger.error(`Plaid Success Handling Error for user ${user?.id}:`, err);
      setConnectionSuccess(false);
      alert(`Error processing bank connection: ${err.message}`);
    }
  };

  // Handle Plaid exit (user canceled or error)
  const handlePlaidExit = (err, metadata) => {
    if (err) {
      logger.error(`Plaid Exit Error for user ${user?.id}:`, {
        message: err.message,
        metadata: metadata
      });

      if (err.error_code) {
        switch (err.error_code) {
          case 'INVALID_LINK_TOKEN':
            alert('Invalid link token. Please try again later.');
            break;
          case 'INVALID_REQUEST':
            alert('There was a problem with the request. Please try again.');
            break;
          case 'INSTITUTION_ERROR':
            alert('There was a problem connecting to this institution. Please try again later.');
            break;
          default:
            alert(`Error: ${err.message || 'Unknown error'}`);
        }
      }
    } else {
      logger.info(`Plaid Link flow exited for user ${user?.id}`, metadata);
    }
  };

  // Add this transformation function to your Dashboard.js file

  /**
   * Transform direct data to match the structure expected by the AI
   * @param {Object} directData - The original direct data
   * @returns {Object} - Transformed data matching AI's expected structure
   */
  const transformDirectDataForAI = (directData) => {
    if (!directData) return null;

    // Create a deep copy to avoid modifying the original
    const originalData = JSON.parse(JSON.stringify(directData));
    const transformedData = {
      // Keep metadata
      institution: originalData.institution,
      lastUpdated: originalData.lastUpdated,
      userId: originalData.userId,

      // Transform accounts
      accounts: originalData.accounts.map(account => ({
        accountId: account.id,
        name: account.name,
        type: account.type,
        subType: account.subtype,
        // CRITICAL: Convert nested balances to flat properties
        balance: account.balances.current,
        availableBalance: account.balances.available,
        creditLimit: account.balances.limit,
        currency: account.balances.isoCurrencyCode,
        mask: account.mask,
        // Add additional metadata to help the AI
        metadata: {
          originalType: account.type,
          originalSubtype: account.subtype
        }
      })),

      // Transform transactions
      transactions: originalData.transactions.map(transaction => ({
        transactionId: transaction.id,
        accountId: transaction.account_id,
        // CRITICAL: Convert amount if needed - ensure expenses are negative
        amount: transaction.amount < 0 ? transaction.amount : -transaction.amount,
        // Flatten category array to string if needed
        category: Array.isArray(transaction.category) ? transaction.category[0] : transaction.category,
        date: transaction.date,
        description: transaction.name,
        // Derive transaction type based on amount
        type: transaction.amount < 0 ? 'expense' : 'income',
        pending: transaction.pending,
        paymentChannel: transaction.payment_channel,
        // Add any additional metadata that might help the AI
        metadata: {
          fullCategory: Array.isArray(transaction.category) ? transaction.category : [transaction.category]
        }
      }))
    };

    return transformedData;
  };

  // Stream insights with EventSource
  const streamInsights = async (inputQuery) => {
    if (!inputQuery.trim()) return;
    setLoading(true);

    try {
      // Add user message
      setChatMessages(prev => [...prev, {
        role: 'user',
        content: inputQuery,
        timestamp: new Date().toISOString()
      }]);

      // Set initial assistant message with loading state
      const tempMessageId = `msg_${Date.now()}`;
      setChatMessages(prev => [...prev, {
        id: tempMessageId,
        role: 'assistant',
        content: '',
        isStreaming: true,
        timestamp: new Date().toISOString()
      }]);

      // Clear input and scroll immediately
      setQuery('');
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });

      // Generate a request ID that includes the user ID for traceability
      const requestId = generateRequestId();
      latestRequestIdRef.current = requestId;

      // CRITICAL FIX: Ensure the correct data is sent based on the active mode
      let dataToSend = null;
      const isUsingPlaidData = integrationMode === 'plaid' && connected;

      if (isUsingPlaidData) {
        // In Plaid mode with connection, use Plaid data (which already has the correct structure)
        dataToSend = financialData ? JSON.parse(JSON.stringify(financialData)) : null;
        logger.info("Using Plaid connected data for API request");
      } else if (integrationMode === 'direct') {
        // In Direct mode, transform the data to match the structure expected by the AI
        const directDataCopy = financialData ? JSON.parse(JSON.stringify(financialData)) : null;
        dataToSend = directDataCopy ? transformDirectDataForAI(directDataCopy) : null;

        logger.info("Using transformed Direct data for API request", {
          accounts: dataToSend?.accounts?.map(a => ({
            name: a.name,
            balance: a.balance,
            type: a.type
          })),
          transactions: dataToSend?.transactions?.length
        });
      } else {
        // Not connected in Plaid mode or unknown mode - use no data
        dataToSend = null;
        logger.info("No financial data available for API request");
      }

      // Update localStorage with current mode for API consistency
      localStorage.setItem('integrationMode', integrationMode);
      localStorage.setItem('plaidConnected', connected ? 'true' : 'false');

      // Create payload with explicit mode flags and only the appropriate data
      const payload = {
        query: inputQuery,
        requestId,
        // Set these flags explicitly based on the active mode
        useConnectedData: isUsingPlaidData,
        userId: user?.id,
        integrationMode,
        useDirectData: integrationMode === 'direct',
        // Only send the appropriate data source based on the active mode
        financialData: dataToSend,
        // Add an explicit mode marker that the backend can check
        dataSourceMode: integrationMode,
        // Add timestamp to help prevent cache issues
        timestamp: new Date().toISOString()
      };

      // Log the payload mode for debugging
      logger.info(`API Payload - Mode: ${integrationMode}, Using real data: ${isUsingPlaidData}, Account count: ${dataToSend?.accounts?.length || 0}`);

      const response = await fetch('/api/insights/stream-prepare', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to prepare streaming request');
      }

      // Create event source for streaming
      const eventSource = new EventSourcePolyfill(`/api/insights/stream?requestId=${requestId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });

      // Handle incoming chunks
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.error) {
            // Handle error in stream
            setChatMessages(prev => prev.map(msg =>
              msg.id === tempMessageId
                ? { ...msg, content: data.message, isStreaming: false }
                : msg
            ));
            eventSource.close();
            return;
          }

          // Special handling for the real-data marker
          if (data.chunk === '<using-real-data>') {
            // IMPORTANT: Only set the real data flag if we're actually in Plaid mode and connected
            if (integrationMode === 'plaid' && connected) {
              setChatMessages(prev => prev.map(msg =>
                msg.id === tempMessageId
                  ? { ...msg, usingRealData: true }
                  : msg
              ));
            }
            return; // Skip this chunk
          }

          // Update message with new content
          setChatMessages(prev => prev.map(msg =>
            msg.id === tempMessageId
              ? { ...msg, content: (msg.content || '') + data.chunk }
              : msg
          ));

          // Auto-scroll
          chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });

          // Close connection if complete
          if (data.isComplete) {
            eventSource.close();

            // Mark streaming as complete
            setChatMessages(prev => prev.map(msg =>
              msg.id === tempMessageId
                ? {
                  ...msg,
                  isStreaming: false,
                  // CRITICAL FIX: Set data source badge based on current mode, not on API response
                  usingRealData: integrationMode === 'plaid' && connected,
                  usingSimulatedData: integrationMode === 'direct'
                }
                : msg
            ));
          }
        } catch (err) {
          logger.error('Error parsing SSE message:', err, event.data);
        }
      };

      // Handle connection errors
      eventSource.onerror = (err) => {
        logger.error('EventSource error:', err);
        eventSource.close();

        // Fallback to non-streaming approach if SSE fails
        fallbackToStandardRequest(inputQuery, requestId, tempMessageId);
      };
    } catch (error) {
      logger.error('Error setting up streaming:', error);

      // Use standard API call as fallback
      fallbackToStandardRequest(inputQuery, generateRequestId(), `msg_${Date.now()}`);
    } finally {
      setLoading(false);
    }
  };

  // Fallback function for when streaming fails
  const fallbackToStandardRequest = async (inputQuery, requestId, tempMessageId) => {
    try {
      // CRITICAL FIX: Ensure proper data mode enforcement in fallback too
      const isUsingPlaidData = integrationMode === 'plaid' && connected;

      // Create data for the current mode
      let dataToSend = null;
      if (isUsingPlaidData) {
        dataToSend = financialData ? JSON.parse(JSON.stringify(financialData)) : null;
      } else if (integrationMode === 'direct') {
        dataToSend = directFinancialData ? JSON.parse(JSON.stringify(directFinancialData)) : null;
      }

      // Custom request options
      const customOptions = {
        integrationMode: integrationMode,
        useConnectedData: isUsingPlaidData,
        useDirectData: integrationMode === 'direct',
        financialData: dataToSend
      };

      // Use the updated generateInsights method with custom options
      const data = await insightsService.generateInsights(inputQuery, requestId, customOptions);

      // Extract text
      const insightText = extractInsightText(data);

      // Update message with full text
      setChatMessages(prev => {
        // Check if the message with tempMessageId already exists
        const messageExists = prev.some(msg => msg.id === tempMessageId);

        if (messageExists) {
          // Update existing message
          return prev.map(msg =>
            msg.id === tempMessageId
              ? {
                ...msg,
                content: insightText,
                isStreaming: false,
                // CRITICAL FIX: Set data source badge based on current mode, not on API response
                usingRealData: integrationMode === 'plaid' && connected,
                usingSimulatedData: integrationMode === 'direct'
              }
              : msg
          );
        } else {
          // Message doesn't exist, add a new one
          return [...prev, {
            id: tempMessageId,
            role: 'assistant',
            content: insightText,
            isStreaming: false,
            // CRITICAL FIX: Set data source badge based on current mode, not on API response
            usingRealData: integrationMode === 'plaid' && connected,
            usingSimulatedData: integrationMode === 'direct',
            timestamp: new Date().toISOString()
          }];
        }
      });
    } catch (error) {
      logger.error('Fallback request error:', error);

      // Get error message
      const errorMessage = getErrorMessage(error);

      // Update or add error message
      setChatMessages(prev => {
        const messageExists = prev.some(msg => msg.id === tempMessageId);

        if (messageExists) {
          return prev.map(msg =>
            msg.id === tempMessageId
              ? { ...msg, content: errorMessage, isStreaming: false }
              : msg
          );
        } else {
          return [...prev, {
            id: tempMessageId,
            role: 'assistant',
            content: errorMessage,
            isStreaming: false,
            timestamp: new Date().toISOString()
          }];
        }
      });
    }
  };

  // Add this helper function if it doesn't already exist
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Helper function to extract insight text
  const extractInsightText = (data) => {
    if (!data || !data.insights) {
      return "I'm unable to generate insights at the moment. Please try again later.";
    }

    if (typeof data.insights === 'string') {
      return data.insights;
    } else if (data.insights.insight) {
      return data.insights.insight;
    } else if (data.insights.text) {
      return data.insights.text;
    } else {
      return JSON.stringify(data.insights);
    }
  };

  // Helper function to get error message based on status code
  const getErrorMessage = (error) => {
    if (error.status === 403) {
      return error.message || "I cannot provide information about potentially harmful or illegal topics. Please ask about legitimate financial matters instead.";
    } else {
      return "I'm sorry, but I encountered an error. Please try again later.";
    }
  };

  // Handle key press in chat input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle sending a message
  const handleSendMessage = () => {
    if (query.trim()) {
      streamInsights(query);
    }
  };

  // Handle using a suggested prompt
  const handleSuggestedPrompt = (prompt) => {
    setQuery(prompt);

    // Focus input field after setting value
    document.getElementById('chat-input')?.focus();
  };

  // Handle improved logout
  const handleLogout = async () => {
    try {
      // First clear Plaid data and financial state
      setConnected(false);
      setInstitution('');
      setShowDataSidebar(false);
      setFinancialData(null);

      // Try to clear session data on server
      if (user?.id) {
        try {
          await api.post('/users/session/clear');
          logger.info(`Session data cleared on server for user ${user.id}`);
        } catch (clearError) {
          logger.error('Failed to clear session data on server:', clearError);
          // Continue with logout even if this fails
        }
      }

      // Finally, perform actual logout
      await logout();

      // Redirect to login
      window.location.href = '/login';
    } catch (error) {
      logger.error('Error during logout:', error);
      // If error occurs, still try to logout
      logout();
    }
  };


  const renderModernActionBar = () => (
    <div className="modern-action-bar">
      <div className="action-buttons">
        <button
          className="modern-btn modern-btn-danger modern-btn-sm" // CHANGED: was "action-btn clear-chat"
          onClick={handleClearChat}
          title="Clear Chat History"
        >
          <i className="bi bi-trash3"></i>
          <span>Clear Chat</span>
        </button>
        <button
          className="modern-btn modern-btn-warning modern-btn-sm" // CHANGED: was "action-btn clear-viz"
          onClick={handleClearVisualizations}
          disabled={chatMessages.length <= 1}
          title="Reset Visualizations"
        >
          <i className="bi bi-bar-chart"></i>
          <span>Reset Charts</span>
        </button>
      </div>

      <div className="chat-info">
        <span className="message-count">{chatMessages.length} messages</span>
      </div>
    </div>
  );

  const renderModernInputArea = () => (
    <div className="modern-input-area">
      <div className="input-container">
        <div className="input-wrapper">
          {InlineQueryHelper ? (
            <InlineQueryHelper
              query={query}
              setQuery={setQuery}
              handleSendMessage={handleSendMessage}
            />
          ) : (
            <div className="input-field-wrapper">
              <textarea
                id="chat-input-modern"
                placeholder="Ask about your financial data..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="modern-chat-input"
                rows={1}
              />
              <div className="input-actions">
                <button
                  className={`modern-btn modern-btn-primary modern-btn-circle ${(!query.trim() || loading) ? 'modern-btn-loading' : ''}`} // CHANGED: was "send-btn ${(!query.trim() || loading) ? 'disabled' : ''}"
                  disabled={!query.trim() || loading}
                  onClick={handleSendMessage}
                  title="Send message"
                >
                  {loading ? null : <i className="bi bi-send-fill"></i>} {/* CHANGED: removed loading spinner div */}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="input-footer">
          <span className="disclaimer">
            This assistant may produce inaccurate information about financial data
          </span>
        </div>
      </div>
    </div>
  );

  // Fix 3: Updated playground section to use corrected components
  const renderPlaygroundSection = () => (
    <div className="modern-playground-layout">
      {/* Modern Header with Glass Effect */}
      <div className="modern-playground-header">
        <div className="header-glass-bg"></div>
        <div className="header-content">
          <div className="header-title-section">
            <div className="title-icon-wrapper">
              <div className="title-icon">
                <i className="bi bi-cpu"></i>
              </div>
              <div className="title-content">
                <h1 className="main-title">Banking Intelligence</h1>
                <p className="subtitle">AI-Powered Financial Insights</p>
              </div>
            </div>
          </div>

          <div className="header-status-section">
            {/* Connection Status Cards */}
            <div className="status-cards">
              {integrationMode === 'plaid' && (
                <div className={`status-card ${connected ? 'connected' : 'disconnected'}`}>
                  <div className="status-indicator">
                    <div className={`status-dot ${connected ? 'success' : 'warning'}`}></div>
                  </div>
                  <div className="status-content">
                    <span className="status-label">Bank Connection</span>
                    <span className="status-value">
                      {connected ? institution : "Disconnected"}
                    </span>
                  </div>
                  {!connected && (
                    <div className="status-action">
                      <PlaidLinkButton
                        onSuccess={(linkData) => {
                          setPlaidStatus('success');
                          handlePlaidSuccess(linkData);
                        }}
                        onExit={(err, metadata) => {
                          if (err) {
                            setPlaidStatus('error');
                            setPlaidError(err.message || 'Error connecting to bank');
                          } else {
                            setPlaidStatus('ready');
                          }
                          handlePlaidExit(err, metadata);
                        }}
                        buttonText="Connect"
                        className="modern-btn modern-btn-primary modern-btn-xs" // CHANGED: was "connect-btn"
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="status-card api-status">
                <div className="status-indicator">
                  <div className="status-dot success"></div>
                </div>
                <div className="status-content">
                  <span className="status-label">API Status</span>
                  <span className="status-value">Online</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Data Source Selector */}
      <div className="modern-data-selector">
        <div className="selector-container">
          <div className="selector-header" onClick={() => setSelectorExpanded(!selectorExpanded)}>
            <div className="selector-title">
              <div className="selector-icon">
                <i className="bi bi-database"></i>
              </div>
              <span>Data Source</span>
            </div>
            <button className="modern-btn modern-btn-ghost modern-btn-xs"> {/* CHANGED: was "selector-toggle" */}
              <i className={`bi bi-chevron-${selectorExpanded ? 'up' : 'down'}`}></i>
            </button>
          </div>

          {selectorExpanded && (
            <div className="selector-content">
              <DataIntegrationModeSelector
                currentMode={integrationMode}
                onSelectMode={handleIntegrationModeChange}
                plaidConnected={connected}
                institution={institution}
              />
            </div>
          )}
        </div>
      </div>

      {/* Success Alert with Modern Design */}
      {connectionSuccess && (
        <div className="modern-alert success-alert">
          <div className="alert-icon">
            <i className="bi bi-check-circle-fill"></i>
          </div>
          <div className="alert-content">
            <h4>Connection Successful!</h4>
            <p>Connected to <strong>{institution}</strong>. You can now ask questions about your financial data.</p>
          </div>
          <button
            className="modern-btn modern-btn-ghost modern-btn-xs" // CHANGED: was "alert-close"
            onClick={() => setConnectionSuccess(false)}
          >
            <i className="bi bi-x"></i>
          </button>
        </div>
      )}

      {/* Modern Chat Interface */}
      <div className="modern-chat-container">
        <div className="chat-interface-modern">
          {/* Enhanced Chat Area */}
          <div className={`chat-area-modern ${showDataSidebar ? 'with-sidebar' : ''}`}>
            {/* Render the corrected action bar */}
            {renderModernActionBar()}

            {/* Enhanced Chat Messages */}
            <div className="modern-chat-messages">
              {chatMessages.map((message, index) => (
                <div
                  key={index}
                  className={`modern-message ${message.role === 'assistant' ? 'assistant' : 'user'}`}
                >
                  <div className="message-avatar-modern">
                    {message.role === 'assistant' ? (
                      <div className="ai-avatar">
                        <img src="/images/logo.png" alt="AI" className="avatar-img" />
                        <div className="avatar-glow"></div>
                      </div>
                    ) : (
                      <div className="user-avatar">
                        <i className="bi bi-person-fill"></i>
                      </div>
                    )}
                  </div>

                  <div className="message-content-modern">
                    <div className="message-header">
                      <span className="message-sender">
                        {message.role === 'assistant' ? 'Assistant' : 'You'}
                      </span>
                      <span className="message-time">
                        {new Date(message.timestamp).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>

                    {/* Data Source Badges */}
                    <div className="message-badges">
                      {message.usingRealData && (
                        <span className="data-badge real-data">
                          <i className="bi bi-bank"></i>
                          Connected Data
                        </span>
                      )}
                      {message.usingSimulatedData && (
                        <span className="data-badge simulated-data">
                          <i className="bi bi-code-square"></i>
                          Demo Data
                        </span>
                      )}
                      {!message.usingRealData && !message.usingSimulatedData &&
                        integrationMode === 'direct' &&
                        !message.isSystemMessage &&
                        message.role === 'assistant' &&
                        !message.isStreaming && (
                          <span className="data-badge simulated-data">
                            <i className="bi bi-code-square"></i>
                            Demo Data
                          </span>
                        )}
                      {!message.usingRealData && !message.usingSimulatedData &&
                        integrationMode === 'plaid' &&
                        connected &&
                        !message.isSystemMessage &&
                        message.role === 'assistant' &&
                        !message.isStreaming && (
                          <span className="data-badge real-data">
                            <i className="bi bi-bank"></i>
                            Connected Data
                          </span>
                        )}
                    </div>

                    <div className="message-text">
                      {message.isStreaming ? (
                        <div className="streaming-content">
                          <pre className="message-pre">{message.content}</pre>
                          <div className="typing-indicator">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                        </div>
                      ) : (
                        <pre className="message-pre">{message.content}</pre>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              <VisualFinanceIntegration
                chatMessages={chatMessages}
                financialData={financialData}
                enabled={visualizationsVisible}
              />

              <div ref={chatEndRef} />
            </div>

            {/* Modern Suggested Prompts */}
            {chatMessages.length < 3 && (
              <div className="modern-suggestions">
                <div className="suggestions-header">
                  <h4>
                    {connected ? "Ask about your connected accounts:" : "Try these questions:"}
                  </h4>
                </div>
                <div className="suggestions-grid">
                  {(connected ? connectedPrompts : suggestedPrompts).map((prompt, index) => (
                    <button
                      key={index}
                      className="modern-btn modern-btn-ghost modern-btn-rounded" // CHANGED: was "suggestion-card"
                      onClick={() => handleSuggestedPrompt(prompt)}
                      style={{
                        padding: '1rem 1.25rem',
                        textAlign: 'left',
                        justifyContent: 'flex-start',
                        whiteSpace: 'normal',
                        height: 'auto',
                        minHeight: '60px'
                      }}
                    >
                      <div className="suggestion-icon">
                        <i className="bi bi-chat-quote"></i>
                      </div>
                      <span className="suggestion-text">{prompt}</span>
                      <div className="suggestion-arrow">
                        <i className="bi bi-arrow-right"></i>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Render the corrected input area */}
            {renderModernInputArea()}
          </div>

          {/* Enhanced Financial Data Sidebar */}
          {showDataSidebar && (
            <div className="modern-sidebar">
              <div className="sidebar-header-mobile">
                <h5>Financial Data</h5>
                <button
                  className="modern-btn modern-btn-ghost modern-btn-xs" // CHANGED: was "sidebar-toggle-mobile"
                  onClick={() => setShowDataSidebar(!showDataSidebar)}
                >
                  <i className="bi bi-x"></i>
                </button>
              </div>

              {integrationMode === 'plaid' ? (
                <PlaidDataSidebar
                  userData={financialData}
                  isVisible={showDataSidebar}
                  isLoading={loadingFinancialData}
                  onRefresh={fetchFinancialData}
                  userId={user?.id}
                />
              ) : (
                <DirectDataIntegration
                  isVisible={showDataSidebar}
                  onDataChange={handleDirectDataChange}
                  initialData={directFinancialData}
                  userId={user?.id}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render sections based on active section
  const renderContent = () => {
    switch (activeSection) {
      case 'playground':
        return renderPlaygroundSection();

      case 'api-keys':
        return <APIKeysManagement />;

      case 'security':
        return <SecuritySettings />;

      case 'data-retention':
        return <DataRetention />;

      case 'documentation':
        return <Documentation />;

      case 'home':
      default:
        return <ModernDashboardHome onNavigate={setActiveSection} />;
    }
  };

  return (
    <div className="dashboard-container">
      {/* Mobile Sidebar Toggle Button - only visible on mobile */}
      <div className="d-lg-none mobile-sidebar-toggle">
        <button
          className="modern-btn modern-btn-primary modern-btn-circle" // CHANGED: was "sidebar-toggle-btn"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label="Toggle sidebar navigation"
        >
          <i className={`bi bi-${sidebarOpen ? 'x' : 'list'}`}></i>
        </button>
      </div>

      {/* Sidebar with mobile toggle */}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h4 className="text-success mb-0">
            <a href="/" className="logo-link">
              <img
                src="/images/logo.png"
                alt="AI Assistant"
                className="sidebar-image-assistant"
              />
            </a>
          </h4>
          {user && (
            <div className="text-white small mt-2 user-info">
              <i className="bi bi-person-circle me-1"></i> {user.email}
              <button
                className="modern-btn modern-btn-ghost modern-btn-xs d-lg-none" // CHANGED: was "d-lg-none close-sidebar-btn"
                onClick={() => setSidebarOpen(false)}
                aria-label="Close sidebar"
              >
                <i className="bi bi-x"></i>
              </button>
            </div>
          )}
        </div>

        <Nav className="sidebar-nav">
          <Nav.Link
            onClick={() => {
              setActiveSection('home');
              if (window.innerWidth < 992) setSidebarOpen(false);
            }}
            className={`sidebar-link ${activeSection === 'home' ? 'active' : ''}`}
          >
            <i className="bi bi-house-door me-2"></i>
            Home
          </Nav.Link>
          <Nav.Link
            onClick={() => {
              setActiveSection('documentation');
              if (window.innerWidth < 992) setSidebarOpen(false);
            }}
            className={`sidebar-link ${activeSection === 'documentation' ? 'active' : ''}`}
          >
            <i className="bi bi-book me-2"></i>
            Documentation
          </Nav.Link>
          <Nav.Link
            onClick={() => {
              setActiveSection('playground');
              if (window.innerWidth < 992) setSidebarOpen(false);
            }}
            className={`sidebar-link ${activeSection === 'playground' ? 'active' : ''}`}
          >
            <i className="bi bi-chat-text me-2"></i>
            Playground
          </Nav.Link>
          <Nav.Link
            onClick={() => {
              setActiveSection('api-keys');
              if (window.innerWidth < 992) setSidebarOpen(false);
            }}
            className={`sidebar-link ${activeSection === 'api-keys' ? 'active' : ''}`}
          >
            <i className="bi bi-key me-2"></i>
            API Keys
          </Nav.Link>

          <hr className="border-secondary my-2" />
          <Nav.Link
            onClick={handleLogout}
            className="sidebar-link text-danger mt-auto"
          >
            <i className="bi bi-box-arrow-right me-2"></i>
            Logout
          </Nav.Link>
        </Nav>
      </div>

      {/* Main content area with class to adjust when sidebar is collapsed on mobile */}
      <div className={`main-content ${sidebarOpen ? '' : 'sidebar-collapsed'}`}>
        {renderContent()}
      </div>

      {/* Privacy Agreement Modal - will automatically show for new users */}
      <PrivacyAgreementModal />
    </div>
  );
};

export default Dashboard;